import './styles/index.scss';
import { Suspense, useEffect, useRef, useState } from 'react';
import { AppRouter } from '@/app/providers/router';
import { PageLoader } from '@/widgets/PageLoader';
import { useExchangeSsoTicketMutation } from '@/entities/Sso';
import { useAppDispatch } from '@/shared/lib/hooks/redux';
import { setIsAuth, setIsAuthLoading } from '@/app/model/slice/appSlice';
import { mapProfileResponseToProfile, setProfileCache, type ProfileApiResponse } from '@/entities/Profile';
import { useCookies } from 'react-cookie';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { detectEnv } from '@/shared/lib/env';

const App = () => {
  const [isSsoPending, setIsSsoPending] = useState(false);
  const [exchangeSsoTicket] = useExchangeSsoTicketMutation();
  const dispatch = useAppDispatch();
  const [, setCookie, removeCookie] = useCookies();
  const alert = useAlert();
  const handledTicketRef = useRef(false);

  useEffect(() => {
    const env = detectEnv();
    const isIndexable = env === 'prod';
    const metaName = 'robots';
    const selector = `meta[name="${metaName}"]`;
    const existingMeta = document.head.querySelector<HTMLMetaElement>(selector);

    if (isIndexable) {
      if (existingMeta && existingMeta.dataset.runtimeManaged === 'true') {
        existingMeta.remove();
      }
      return;
    }

    if (existingMeta) {
      existingMeta.setAttribute('content', 'noindex,nofollow');
      existingMeta.dataset.runtimeManaged = 'true';
      return;
    }

    const meta = document.createElement('meta');
    meta.name = metaName;
    meta.content = 'noindex,nofollow';
    meta.dataset.runtimeManaged = 'true';
    document.head.appendChild(meta);
  }, []);

  useEffect(() => {
    if (handledTicketRef.current) {
      return;
    }
    handledTicketRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('sso_ticket');
    if (!ticket) {
      return;
    }

    params.delete('sso_ticket');
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
    window.history.replaceState({}, document.title, newUrl);

    setIsSsoPending(true);
    dispatch(setIsAuthLoading(true));

    exchangeSsoTicket({ ticket })
      .unwrap()
      .then(({ token, ...profileData }) => {
        const profile = mapProfileResponseToProfile(profileData as ProfileApiResponse);

        removeCookie('token', { path: '/' });
        sessionStorage.removeItem('token');
        setCookie('token', token, { path: '/' });

        dispatch(setProfileCache(profile));
        dispatch(setIsAuth(true));
      })
      .catch(() => {
        removeCookie('token', { path: '/' });
        sessionStorage.removeItem('token');
        localStorage.removeItem('balanceWarningDismissed');
        dispatch(setIsAuth(false));
        alert('Не удалось подтвердить SSO билет, авторизуйтесь вручную.', true);
      })
      .finally(() => {
        setIsSsoPending(false);
        dispatch(setIsAuthLoading(false));
      });
  }, [alert, dispatch, exchangeSsoTicket, removeCookie]);

  return <Suspense fallback={<PageLoader />}>{isSsoPending ? <PageLoader /> : <AppRouter />}</Suspense>;
};

export default App;
